import React, { useState, useEffect } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getFoodCourtItemById, updateFoodCourtItem, deleteFoodCourtItem } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams } from "react-router-dom";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  Utensils,
  DollarSign,
  Phone,
  Mail,
  Save,
  Tag,
  Clock,
  Info,
  Trash2,
  AlertCircle,
} from "lucide-react";

interface FoodCourtFormData {
  title: string;
  description: string;
  category: "FOOD" | "BEVERAGE" | "DESSERT" | "SNACK";
  cuisine: string;
  dietaryInfo: string[];
  price: string;
  businessHours: string;
  contactPhone: string;
  contactEmail: string;
}

const DIETARY_OPTIONS = [
  { value: "VEGETARIAN", label: "Vegetarian" },
  { value: "VEGAN", label: "Vegan" },
  { value: "GLUTEN_FREE", label: "Gluten Free" },
  { value: "HALAL", label: "Halal" },
  { value: "KOSHER", label: "Kosher" },
];

export default function EditFoodCourtPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();

  const { data: item, isLoading } = useQuery(getFoodCourtItemById, { itemId: itemId! });
  const updateFoodCourtFn = useAction(updateFoodCourtItem);
  const deleteFoodCourtFn = useAction(deleteFoodCourtItem);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState<FoodCourtFormData>({
    title: "",
    description: "",
    category: "FOOD",
    cuisine: "",
    dietaryInfo: [],
    price: "",
    businessHours: "",
    contactPhone: "",
    contactEmail: "",
  });

  useEffect(() => {
    if (item) {
      const meta = item.metadata as any;
      setFormData({
        title: item.title || "",
        description: item.description || "",
        category: meta?.category || "FOOD",
        cuisine: meta?.cuisine || "",
        dietaryInfo: meta?.dietaryInfo || [],
        price: meta?.price ? meta.price.toString() : "0",
        businessHours: meta?.businessHours || "",
        contactEmail: meta?.contactEmail || (meta?.contactInfo?.includes("@") ? meta.contactInfo.split("|")[0].trim() : ""),
        contactPhone: meta?.contactPhone || (meta?.contactInfo?.includes("|") ? meta.contactInfo.split("|")[1].trim() : ""),
      });
    }
  }, [item]);

  const formatPhoneNumber = (value: string) => {
    const numbers = value?.replace(/\D/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleChange = (field: keyof FoodCourtFormData, value: any) => {
    if (field === "contactPhone") {
      value = formatPhoneNumber(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const toggleDietary = (value: string) => {
    setFormData(prev => ({
      ...prev,
      dietaryInfo: prev.dietaryInfo.includes(value)
        ? prev.dietaryInfo.filter(v => v !== value)
        : [...prev.dietaryInfo, value]
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = "Name is required";
    if (!formData.description.trim()) newErrors.description = "Description is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await updateFoodCourtFn({
        itemId: itemId!,
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        cuisine: formData.cuisine.trim(),
        dietaryInfo: formData.dietaryInfo,
        price: parseFloat(formData.price) || 0,
        contactInfo: `${formData.contactEmail}${formData.contactPhone ? ` | ${formData.contactPhone}` : ""}`,
        metadata: {
          businessHours: formData.businessHours.trim(),
        }
      });
      handleGoBack();
    } catch (error) {
      console.error("Error updating food court item:", error);
      setErrors({ submit: "Failed to update item." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this food entry?")) return;
    try {
      await deleteFoodCourtFn({ itemId: itemId! });
      handleGoBack();
    } catch (error) {
      console.error("Error deleting food court item:", error);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/food-court`);
    } else {
      navigate("/food-court");
    }
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">You must be logged in to edit food entries.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (isLoading) {
    return (
      <IndividualEditPageLayout title="Loading Entry...">
        <div className="p-6 text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading food entry details...</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (!item) {
    return (
      <IndividualEditPageLayout title="Entry Not Found" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Entry Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            The food entry you're looking for doesn't exist.
          </p>
          <button
            onClick={handleGoBack}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        onClick={handleSubmit}
        disabled={isSubmitting}
        icon={Save}
      >
        {isSubmitting ? "Saving..." : "Save Changes"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleGoBack}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Category" hideOnMobile>
        <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-xl w-full">
          {["FOOD", "BEVERAGE", "DESSERT", "SNACK"].map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => handleChange("category", cat as any)}
              className={`py-2 rounded-lg text-xs font-bold transition-all ${
                formData.category === cat ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {cat.charAt(0) + cat.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </SidebarSection>
      <SidebarSection title="Danger Zone" hideOnMobile>
        <button
          onClick={handleDelete}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 text-red-600 bg-red-50 border border-red-100 rounded-xl hover:bg-red-100 transition-colors font-semibold"
        >
          <Trash2 className="w-4 h-4" /> Delete Entry
        </button>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title="Edit Food Item"
      subtitle={`Manage ${formData.title}`}
      icon={Utensils}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="food-form" onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Item or Venue Name</label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><Info className="w-4 h-4 text-orange-600" /> Menu Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Cuisine</label>
                <input
                  type="text"
                  value={formData.cuisine}
                  onChange={(e) => handleChange("cuisine", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-orange-500/20 transition-all font-medium"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Price ($)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleChange("price", e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><Clock className="w-4 h-4 text-blue-600" /> Business Info</h3>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Hours</label>
              <input
                type="text"
                value={formData.businessHours}
                onChange={(e) => handleChange("businessHours", e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Dietary Info</label>
          <div className="flex flex-wrap gap-2">
            {DIETARY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleDietary(opt.value)}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-all border ${
                  formData.dietaryInfo.includes(opt.value)
                    ? "bg-slate-700 text-white border-slate-700 shadow-sm"
                    : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><Mail className="w-4 h-4 text-blue-600" /> Contact Info</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                value={formData.contactEmail}
                readOnly
                className="w-full pl-10 pr-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 cursor-not-allowed"
              />
            </div>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={formData.contactPhone}
                onChange={(e) => handleChange("contactPhone", e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
              />
            </div>
          </div>
        </div>
        {errors.submit && <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm font-medium">{errors.submit}</div>}
      </form>
    </IndividualEditPageLayout>
  );
}
